import { useState, useEffect } from 'react';
import { getCertifications } from '../../services/certificationService';
import { setUserTrial } from '../../services/profileService';
import type { Certification } from '../../types/certification';
import type { UserTrial } from '../../types/profile';

interface SetUserTrialModalProps {
  profileId: string;
  trials?: UserTrial[];
  onClose: () => void;
  onSuccess: () => void;
}

function isoToLocalDatetime(iso: string): string {
  const d = new Date(iso);
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
}

export function SetUserTrialModal({ profileId, trials, onClose, onSuccess }: SetUserTrialModalProps) {
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [certificationId, setCertificationId] = useState('');
  const [expires, setExpires] = useState('');
  const [fetching, setFetching] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getCertifications()
      .then((certs) => {
        setCertifications(certs);
        if (certs.length === 1) {
          setCertificationId(certs[0].id);
          const existing = trials?.find((t) => t.certificationId === certs[0].id);
          if (existing) setExpires(isoToLocalDatetime(existing.expires));
        }
      })
      .catch(() => setError('Failed to load certifications'))
      .finally(() => setFetching(false));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!certificationId || !expires) return;

    setError('');
    setLoading(true);
    try {
      await setUserTrial(profileId, {
        certificationId,
        expires: new Date(expires).toISOString(),
      });
      onSuccess();
    } catch {
      setError('Failed to set user trial');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-lg w-full max-w-md mx-4">
        <form onSubmit={handleSubmit}>
          <div className="px-6 pt-6 pb-4">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Set User Trial</h2>
            {fetching ? (
              <p className="text-sm text-gray-400">Loading...</p>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className={labelClass}>Certification *</label>
                  <select
                    value={certificationId}
                    onChange={(e) => {
                      const id = e.target.value;
                      setCertificationId(id);
                      const existing = trials?.find((t) => t.certificationId === id);
                      if (existing) setExpires(isoToLocalDatetime(existing.expires));
                    }}
                    required
                    className={inputClass}
                  >
                    <option value="">Select certification</option>
                    {certifications.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Expires *</label>
                  <input
                    type="datetime-local"
                    value={expires}
                    onChange={(e) => setExpires(e.target.value)}
                    required
                    className={inputClass}
                  />
                </div>
              </div>
            )}
            {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
          </div>
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || fetching || !certificationId || !expires}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Saving...' : 'Set Trial'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
